import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ArrowLeft, Save, FileText, Upload, Trash2, ExternalLink, Plus,
  Calendar, MapPin, Building2, User, AlertTriangle, CheckCircle,
  Clock, DollarSign, Award, Users, FileUp
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = ['Overview', 'Schedule', 'Scope', 'Competitors', 'Result', 'Documents', 'Notes'];

const STATUS_COLORS = {
  'Lead': 'bg-gray-100 text-gray-700', 'Qualified': 'bg-blue-100 text-blue-700',
  'Bidding': 'bg-amber-100 text-amber-700', 'Proposal Submitted': 'bg-purple-100 text-purple-700',
  'Negotiation': 'bg-indigo-100 text-indigo-700', 'Awarded': 'bg-emerald-100 text-emerald-700',
  'Lost': 'bg-red-100 text-red-700',
};

const fmt = (v) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) : '';

function fileUrl(servePath) { return `${API}/api${encodeURI(servePath)}`; }

// ── Reusable field components ────────────────────────────
const Field = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? 'col-span-2' : ''}>
    <Label className="text-xs text-slate-500 mb-1 block">{label}</Label>
    {children}
  </div>
);

const TextInput = ({ label, value, onChange, placeholder, span, type = 'text', ...props }) => (
  <Field label={label} span={span}>
    <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="h-8 text-sm" {...props} />
  </Field>
);

const SelectInput = ({ label, value, onChange, options, placeholder, span }) => (
  <Field label={label} span={span}>
    <select value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-1.5 border rounded-md text-sm bg-white h-8">
      <option value="">{placeholder || 'Select...'}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${checked ? 'bg-blue-900' : 'bg-gray-300'}`}>
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
    </button>
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);

const TextArea = ({ label, value, onChange, rows = 3, span = 2, placeholder }) => (
  <Field label={label} span={span}>
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
      className="w-full px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
  </Field>
);

const CurrencyInput = ({ label, value, onChange, placeholder = '0.00', span, testId }) => {
  const [raw, setRaw] = useState('');
  const [focused, setFocused] = useState(false);
  const display = value !== '' && value != null
    ? Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';
  return (
    <Field label={label} span={span}>
      <div className="relative">
        <span className="absolute left-3 top-1.5 text-gray-400 text-sm">$</span>
        <Input type="text" data-testid={testId}
          value={focused ? raw : display}
          onFocus={() => { setFocused(true); setRaw(value !== '' && value != null ? String(value) : ''); }}
          onBlur={() => setFocused(false)}
          onChange={e => { const v = e.target.value.replace(/[^0-9.,]/g, ''); setRaw(v); const parsed = parseFloat(v.replace(/,/g, '')); onChange(isNaN(parsed) ? null : parsed); }}
          placeholder={placeholder} className="pl-7 h-8 text-sm" />
      </div>
    </Field>
  );
};

// ── Overview Tab ─────────────────────────────────────────
const OverviewTab = ({ bid, onChange, enums }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
    <TextInput label="Project Name" value={bid.project_name} onChange={v => onChange('project_name', v)} span={2} />
    <SelectInput label="Status" value={bid.status} onChange={v => onChange('status', v)} options={enums.statuses || []} />
    <TextInput label="Agency / Owner" value={bid.agency} onChange={v => onChange('agency', v)} />
    <TextInput label="Engineer / Consultant" value={bid.engineer} onChange={v => onChange('engineer', v)} />
    <TextInput label="Location" value={bid.location} onChange={v => onChange('location', v)} />
    <TextInput label="Project Type" value={bid.project_type} onChange={v => onChange('project_type', v)} />
    <SelectInput label="Bidding As" value={bid.bidding_as} onChange={v => onChange('bidding_as', v)} options={['Prime', 'Sub']} />
    <TextInput label="License Requirement" value={bid.license_requirement} onChange={v => onChange('license_requirement', v)} placeholder="e.g., A, B, C-33, C-61/D-12" />
    <TextInput label="Market Sector" value={bid.market_sector} onChange={v => onChange('market_sector', v)} />
    <SelectInput label="Delivery Method" value={bid.delivery_method} onChange={v => onChange('delivery_method', v)} options={enums.delivery_methods || []} />
    <TextInput label="Source / Bid Board" value={bid.source} onChange={v => onChange('source', v)} />
    <TextInput label="Invitation Source" value={bid.invitation_source} onChange={v => onChange('invitation_source', v)} />
    <TextInput label="Estimator" value={bid.estimator_name} onChange={v => onChange('estimator_name', v)} />
    <TextInput label="Project Executive / Reviewer" value={bid.project_executive} onChange={v => onChange('project_executive', v)} />
    <SelectInput label="Go / No-Go" value={bid.go_nogo} onChange={v => onChange('go_nogo', v)} options={enums.go_nogo || []} />
    <TextInput label="Go / No-Go Reason" value={bid.go_nogo_reason} onChange={v => onChange('go_nogo_reason', v)} />
    <TextInput label="Probability Score (%)" value={bid.probability_score} onChange={v => onChange('probability_score', v ? parseInt(v) || null : null)} type="number" />
    <CurrencyInput label="Engineer's Estimate" value={bid.engineers_estimate} onChange={v => onChange('engineers_estimate', v)} placeholder="0.00" testId="bid-engineers-estimate" />
    <div className="col-span-2 lg:col-span-3 flex flex-wrap gap-x-8 gap-y-2 pt-2">
      <Toggle label="Bid Bond Required" checked={bid.bid_bond_required} onChange={v => onChange('bid_bond_required', v)} />
      <Toggle label="Prevailing Wage" checked={bid.prevailing_wage} onChange={v => onChange('prevailing_wage', v)} />
      <Toggle label="Planholders Available" checked={bid.planholders_available} onChange={v => onChange('planholders_available', v)} />
    </div>
    <TextInput label="Spec Section" value={bid.spec_section} onChange={v => onChange('spec_section', v)} />
    <TextInput label="Manufacturer Basis of Design" value={bid.manufacturer_basis_of_design} onChange={v => onChange('manufacturer_basis_of_design', v)} />
    <TextInput label="Incumbent Contractor" value={bid.incumbent_contractor_name} onChange={v => onChange('incumbent_contractor_name', v)} />
  </div>
);

// ── Schedule Tab ─────────────────────────────────────────
const ScheduleTab = ({ bid, onChange }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
    <TextInput label="Bid Due Date & Time" value={bid.bid_due_at ? bid.bid_due_at.slice(0, 16) : ''} onChange={v => onChange('bid_due_at', v || null)} type="datetime-local" />
    <TextInput label="Timezone" value={bid.timezone} onChange={v => onChange('timezone', v)} placeholder="America/Los_Angeles" />
    <TextInput label="Pre-Bid Meeting" value={bid.prebid_meeting_at ? bid.prebid_meeting_at.slice(0, 16) : ''} onChange={v => onChange('prebid_meeting_at', v || null)} type="datetime-local" />
    <TextInput label="Site Walk" value={bid.site_walk_at ? bid.site_walk_at.slice(0, 16) : ''} onChange={v => onChange('site_walk_at', v || null)} type="datetime-local" />
    <TextInput label="Submitted At" value={bid.submitted_at ? bid.submitted_at.slice(0, 16) : ''} onChange={v => onChange('submitted_at', v || null)} type="datetime-local" />
    <div />
    <div className="col-span-2 lg:col-span-3 mt-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">Addenda Log</h4>
      {(bid.addenda || []).length === 0 ? <p className="text-sm text-gray-400">No addenda recorded</p>
      : <div className="space-y-2">
        {bid.addenda.map((a, i) => (
          <div key={a.id || i} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border text-sm">
            <Badge className="bg-blue-100 text-blue-700 text-xs">#{a.number}</Badge>
            <span className="font-medium flex-1">{a.title || a.description || 'Untitled'}</span>
            <span className="text-xs text-gray-400">{a.received_date || ''}</span>
            {a.requires_estimate_review && <Badge className="bg-amber-100 text-amber-700 text-xs">Review Needed</Badge>}
            {a.acknowledged && <CheckCircle size={14} className="text-emerald-500" />}
          </div>
        ))}
      </div>}
      <AddAddendumForm bid={bid} onChange={onChange} />
    </div>
  </div>
);

const AddAddendumForm = ({ bid, onChange }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: '', received_date: '', requires_estimate_review: false });
  const add = () => {
    const addenda = [...(bid.addenda || []), {
      id: `add_${Date.now()}`, number: (bid.addenda || []).length + 1,
      ...form, acknowledged: false, attachment_id: null, description: form.title,
    }];
    onChange('addenda', addenda);
    onChange('addenda_count', addenda.length);
    setForm({ title: '', received_date: '', requires_estimate_review: false });
    setShow(false);
  };
  if (!show) return <Button variant="outline" size="sm" onClick={() => setShow(true)} className="mt-2"><Plus size={14} className="mr-1" /> Add Addendum</Button>;
  return (
    <div className="flex items-end gap-2 mt-3 p-3 border rounded-lg bg-white">
      <TextInput label="Title" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} />
      <TextInput label="Received Date" value={form.received_date} onChange={v => setForm(f => ({ ...f, received_date: v }))} type="date" />
      <Toggle label="Review Needed" checked={form.requires_estimate_review} onChange={v => setForm(f => ({ ...f, requires_estimate_review: v }))} />
      <Button size="sm" onClick={add} disabled={!form.title} className="bg-blue-900 hover:bg-blue-800">Add</Button>
      <Button variant="ghost" size="sm" onClick={() => setShow(false)}>Cancel</Button>
    </div>
  );
};

// ── Scope Tab ────────────────────────────────────────────
const ScopeTab = ({ bid, onChange, enums }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
    <SelectInput label="Work Type" value={bid.work_type} onChange={v => onChange('work_type', v)} options={enums.work_types || []} />
    <TextInput label="Substrate" value={bid.substrate} onChange={v => onChange('substrate', v)} placeholder="Carbon steel, concrete, etc." />
    <TextInput label="Environment" value={bid.environment} onChange={v => onChange('environment', v)} placeholder="Potable water, chemical, etc." />
    <TextInput label="Asset Type" value={bid.asset_type} onChange={v => onChange('asset_type', v)} placeholder="Tank, pipe, clarifier, etc." />
    <TextInput label="Coating / Lining System Family" value={bid.coating_system_family} onChange={v => onChange('coating_system_family', v)} />
    <TextInput label="Access Complexity" value={bid.access_complexity} onChange={v => onChange('access_complexity', v)} placeholder="Low, Moderate, High" />
    <div className="col-span-2 lg:col-span-3 flex flex-wrap gap-x-8 gap-y-2 pt-2 pb-2">
      <Toggle label="Shutdown Required" checked={bid.shutdown_required} onChange={v => onChange('shutdown_required', v)} />
      <Toggle label="Confined Space" checked={bid.confined_space} onChange={v => onChange('confined_space', v)} />
      <Toggle label="Dehumidification Likely" checked={bid.dehumidification_likely} onChange={v => onChange('dehumidification_likely', v)} />
      <Toggle label="Containment Likely" checked={bid.containment_likely} onChange={v => onChange('containment_likely', v)} />
    </div>
    <TextArea label="Scope Summary" value={bid.scope_summary} onChange={v => onChange('scope_summary', v)} placeholder="Describe the scope of work..." />
    <TextArea label="Exclusions Summary" value={bid.exclusions_summary} onChange={v => onChange('exclusions_summary', v)} placeholder="What is excluded from the bid..." />
    <TextArea label="Qualifications Summary" value={bid.qualifications_summary} onChange={v => onChange('qualifications_summary', v)} placeholder="Qualifications and assumptions..." />
    <TextInput label="Strategic Fit Score (1-10)" value={bid.strategic_fit_score} onChange={v => onChange('strategic_fit_score', v ? parseInt(v) || null : null)} type="number" />
  </div>
);

// ── Competitors Tab ──────────────────────────────────────
const CompetitorsTab = ({ bid, onChange }) => {
  const competitors = bid.competitors || [];
  const add = () => {
    onChange('competitors', [...competitors, { id: `comp_${Date.now()}`, name: '', bid_amount: null, bid_rank: null, is_awarded: false, company_id: null, scope_notes: '', notes: '' }]);
  };
  const update = (idx, field, value) => {
    const updated = [...competitors];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange('competitors', updated);
  };
  const remove = (idx) => {
    onChange('competitors', competitors.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-slate-700">Known Competitors</h4>
        <Button variant="outline" size="sm" onClick={add} data-testid="add-competitor-btn"><Plus size={14} className="mr-1" /> Add Competitor</Button>
      </div>
      {competitors.length === 0 ? <p className="text-sm text-gray-400 py-4">No competitors tracked yet.</p>
      : <div className="space-y-2">
        {competitors.map((c, i) => (
          <div key={c.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border" data-testid={`competitor-${i}`}>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
              <Input placeholder="Competitor Name" value={c.name || ''} onChange={e => update(i, 'name', e.target.value)} className="h-8 text-sm" />
              <div className="relative">
                <span className="absolute left-2.5 top-1.5 text-gray-400 text-sm z-10">$</span>
                <Input placeholder="0.00" type="number" step="0.01" value={c.bid_amount || ''} onChange={e => update(i, 'bid_amount', e.target.value ? parseFloat(e.target.value) : null)} className="h-8 text-sm pl-7" />
              </div>
              <Input placeholder="Rank" type="number" value={c.bid_rank || ''} onChange={e => update(i, 'bid_rank', e.target.value ? parseInt(e.target.value) : null)} className="h-8 text-sm" />
              <Input placeholder="Scope Notes" value={c.scope_notes || ''} onChange={e => update(i, 'scope_notes', e.target.value)} className="h-8 text-sm" />
            </div>
            <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
              <input type="checkbox" checked={c.is_awarded || false} onChange={e => update(i, 'is_awarded', e.target.checked)} className="rounded" /> Won
            </label>
            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => remove(i)}><Trash2 size={14} /></Button>
          </div>
        ))}
      </div>}
    </div>
  );
};

// ── Currency Field (focus/blur for decimal support) ──────
const CurrencyField = ({ label, value, onChange, testId }) => {
  const [raw, setRaw] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const display = value != null ? Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  return (
    <Field label={label}>
      <div className="relative">
        <span className="absolute left-3 top-1.5 text-gray-400 text-sm">$</span>
        <Input className="h-8 text-sm pl-7" data-testid={testId}
          value={focused ? raw : display}
          onFocus={() => { setFocused(true); setRaw(value != null ? String(value) : ''); }}
          onBlur={() => setFocused(false)}
          onChange={e => { const v = e.target.value.replace(/[^0-9.,]/g, ''); setRaw(v); onChange(parseFloat(v.replace(/,/g, '')) || null); }}
          placeholder="0.00" />
      </div>
    </Field>
  );
};

// ── Result / Award Tab ───────────────────────────────────
const ResultTab = ({ bid, onChange, enums }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
    <SelectInput label="Award Status" value={bid.award_status} onChange={v => onChange('award_status', v)} options={enums.award_statuses || []} />
    <CurrencyField label="Our Bid Amount" value={bid.bid_amount} onChange={v => onChange('bid_amount', v)} testId="bid-amount-input" />
    <TextInput label="Bid Rank" value={bid.bid_rank} onChange={v => onChange('bid_rank', v ? parseInt(v) : null)} type="number" />
    <TextInput label="Awarded Contractor" value={bid.awarded_contractor} onChange={v => onChange('awarded_contractor', v)} />
    <CurrencyField label="Awarded Amount" value={bid.awarded_amount} onChange={v => onChange('awarded_amount', v)} testId="awarded-amount-input" />
    <TextInput label="Delta from Our Bid" value={bid.delta_from_our_bid} onChange={() => {}} disabled />
    <TextInput label="Award Date" value={bid.award_date} onChange={v => onChange('award_date', v)} type="date" />
    <SelectInput label="Lost Reason" value={bid.lost_reason} onChange={v => onChange('lost_reason', v)} options={enums.lost_reasons || []} />
    <div />
    <TextArea label="Accepted Alternates" value={bid.accepted_alternates} onChange={v => onChange('accepted_alternates', v)} rows={2} />
    <TextArea label="Excluded Alternates" value={bid.excluded_alternates} onChange={v => onChange('excluded_alternates', v)} rows={2} />
    <TextArea label="Lost Notes" value={bid.lost_notes} onChange={v => onChange('lost_notes', v)} rows={2} />
    <TextArea label="Post-Bid Follow-Up Notes" value={bid.post_bid_notes} onChange={v => onChange('post_bid_notes', v)} rows={2} />
  </div>
);

// ── Documents Tab ────────────────────────────────────────
const DocumentsTab = ({ bid, bidId, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [fileType, setFileType] = useState('plans');
  const FILE_TYPES = ['plans', 'specs', 'addenda', 'proposal', 'bid_results', 'bonding', 'project_calculations', 'subcontractor_quotes', 'other'];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/crm/bids/${bidId}/files?file_type=${fileType}`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error();
      toast.success('File uploaded');
      onRefresh();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await fetch(`${API}/api/crm/bids/${bidId}/files/${fileId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('File deleted');
      onRefresh();
    } catch { toast.error('Delete failed'); }
  };

  const grouped = {};
  (bid.attachments || []).forEach(a => {
    const t = a.file_type || 'other';
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(a);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={fileType} onChange={e => setFileType(e.target.value)} className="px-3 py-1.5 border rounded-md text-sm bg-white">
          {FILE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}</option>)}
        </select>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
          <Button variant="outline" size="sm" asChild disabled={uploading}>
            <span><Upload size={14} className="mr-1.5" />{uploading ? 'Uploading...' : 'Upload File'}</span>
          </Button>
        </label>
      </div>
      {Object.keys(grouped).length === 0 ? <p className="text-sm text-gray-400 py-4">No documents attached.</p>
      : Object.entries(grouped).map(([type, files]) => (
        <div key={type}>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{type.replace('_', ' ')}</h4>
          <div className="space-y-1.5">
            {files.map(f => (
              <div key={f.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border" style={{ minHeight: '40px' }}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText size={16} className="text-red-500 flex-shrink-0" />
                  <button onClick={() => window.open(fileUrl(f.serve_path), '_blank')} className="text-sm text-indigo-600 hover:underline truncate text-left">{f.filename}</button>
                  <span className="text-xs text-gray-400 flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                </div>
                <div className="flex gap-1 flex-shrink-0 ml-2">
                  <Button variant="ghost" size="sm" onClick={() => window.open(fileUrl(f.serve_path), '_blank')}><ExternalLink size={14} /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(f.id)}><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Notes Tab ────────────────────────────────────────────
const NotesTab = ({ bid, onChange }) => (
  <div className="grid grid-cols-1 gap-4">
    <TextArea label="Notes (visible to team)" value={bid.notes} onChange={v => onChange('notes', v)} rows={6} span={2} placeholder="General notes about this bid..." />
    <TextArea label="Internal Notes (staff only)" value={bid.internal_notes} onChange={v => onChange('internal_notes', v)} rows={6} span={2} placeholder="Internal strategy, pricing notes..." />
  </div>
);

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const CRMBidDetail = ({ bidId, enums, onBack }) => {
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('Overview');

  const loadBid = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/bids/${bidId}`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      setBid(await res.json());
    } catch { toast.error('Failed to load bid'); onBack(); }
    finally { setLoading(false); }
  }, [bidId, onBack]);

  useEffect(() => { loadBid(); }, [loadBid]);

  const handleChange = (field, value) => {
    setBid(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/bids/${bidId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bid),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Save failed');
        setSaving(false);
        return;
      }
      const updated = await res.json();
      setBid(updated);
      setDirty(false);
      toast.success('Bid saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Archive this bid? It can be recovered later.')) return;
    try {
      await fetch(`${API}/api/crm/bids/${bidId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Bid archived');
      onBack();
    } catch { toast.error('Failed to archive'); }
  };

  if (loading || !bid) {
    return <div className="text-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900 mx-auto" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="bid-back-btn"><ArrowLeft size={16} className="mr-1" /> Bids</Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">{bid.project_name}</h2>
              <Badge className={STATUS_COLORS[bid.status] || 'bg-gray-100'}>{bid.status}</Badge>
            </div>
            <p className="text-xs text-gray-400 font-mono">{bid.bid_number} | {bid.agency || 'No agency'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="text-red-500" onClick={handleDelete} data-testid="bid-delete-btn"><Trash2 size={14} className="mr-1" /> Archive</Button>
          <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} disabled={saving || !dirty} data-testid="bid-save-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b -mb-px overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            data-testid={`bid-tab-${tab.toLowerCase()}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Card>
        <CardContent className="pt-5">
          {activeTab === 'Overview' && <OverviewTab bid={bid} onChange={handleChange} enums={enums} />}
          {activeTab === 'Schedule' && <ScheduleTab bid={bid} onChange={handleChange} />}
          {activeTab === 'Scope' && <ScopeTab bid={bid} onChange={handleChange} enums={enums} />}
          {activeTab === 'Competitors' && <CompetitorsTab bid={bid} onChange={handleChange} />}
          {activeTab === 'Result' && <ResultTab bid={bid} onChange={handleChange} enums={enums} />}
          {activeTab === 'Documents' && <DocumentsTab bid={bid} bidId={bidId} onRefresh={loadBid} />}
          {activeTab === 'Notes' && <NotesTab bid={bid} onChange={handleChange} />}
        </CardContent>
      </Card>
    </div>
  );
};

export default CRMBidDetail;
